import React from 'react';
import styled from 'astroturf/react';

import { Navbar, LinkItem } from 'js/design_system/nav/Navbar';
import { ButtonLink } from 'js/design_system/link/ButtonLink';

import { CoScientistCta } from './CoScientistCta';
import { SECTION_IDS } from './types';

const LogoLink = styled.a`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-12, 12px);

  &,
  &:hover,
  &:active,
  &:focus {
    text-decoration: none;
    color: var(--text-primary, #141413);
  }
`;

const Wordmark = styled.img<{ compact?: boolean }>`
  display: block;
  width: auto;
  height: 18px;

  &.compact {
    height: 14px;
  }
`;

const ProductName = styled.span`
  @import 'stylesheets/design_system/font';

  @include serif__small;
  white-space: nowrap;
`;

type LogoProps = {
  href: string;
  academiaLogo: string;
  compact?: boolean;
};

const Logo: React.FC<LogoProps> = ({ href, academiaLogo, compact }) => (
  <LogoLink href={href}>
    <Wordmark src={academiaLogo} alt="Academia" compact={compact} />
    <ProductName>Co-scientist</ProductName>
  </LogoLink>
);

type CoScientistLandingNavbarProps = {
  currentPage: 'home' | 'pricing';
  homeUrl: string;
  pricingUrl: string;
  ctaUrl: string;
  hasAccess: boolean;
  signInUrl: string | null;
  academiaLogo: string;
  messagesCount: number | null;
  inDevStyle?: boolean;
};

/* Navbar for the Co-scientist marketing and pricing pages, modeled on the Journals navbar */
const CoScientistLandingNavbar: React.FC<CoScientistLandingNavbarProps> = ({
  currentPage,
  homeUrl,
  pricingUrl,
  ctaUrl,
  hasAccess,
  signInUrl,
  academiaLogo,
  messagesCount,
  inDevStyle,
}) => {
  const sectionHref = (id: string) =>
    currentPage === 'home' ? `#${id}` : `${homeUrl}#${id}`;

  return (
    <Navbar
      logoSmall={<Logo href={homeUrl} academiaLogo={academiaLogo} compact />}
      logoLarge={<Logo href={homeUrl} academiaLogo={academiaLogo} />}
      items={
        <>
          <LinkItem href={sectionHref(SECTION_IDS.howItWorks)}>
            How it works
          </LinkItem>
          <LinkItem href={sectionHref(SECTION_IDS.dataPrivacy)}>
            Data privacy
          </LinkItem>
          <LinkItem href={sectionHref(SECTION_IDS.faqs)}>FAQs</LinkItem>
          <LinkItem href={pricingUrl}>
            {/* LinkItem takes no class or aria props; co_scientist_landing.scss styles this marker */}
            <span
              className={
                currentPage === 'pricing'
                  ? 'co-scientist-nav-current'
                  : undefined
              }
            >
              Pricing
            </span>
          </LinkItem>
        </>
      }
      ctas={
        <>
          <CoScientistCta href={ctaUrl} size="sm">
            {hasAccess ? 'Open Co-scientist' : 'Start trial'}
          </CoScientistCta>
          {signInUrl ? (
            <ButtonLink href={signInUrl} size="sm" secondary rel="nofollow">
              Sign in
            </ButtonLink>
          ) : null}
        </>
      }
      messagesCount={messagesCount}
      unreadMentionsCount={null}
      inDevStyle={inDevStyle}
    />
  );
};

export default CoScientistLandingNavbar;
